import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { empSkillService } from '@/services/empSkillService'
import { employeesService } from '@/services/employeesService'
import type { SkillVm, SkillTypeOption } from '@/api/types/empSkill.types'
import { showError, showSuccess } from '@/lib/swal'

export const EmpSkillEdit: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId
  const isEditing = Boolean(id && id !== 'new')

  const [loading, setLoading] = useState(false)
  const [employees, setEmployees] = useState<{ id?: number; empCode?: string; empFirstNameEn?: string; empSecondNameEn?: string; empLastNameEn?: string }[]>([])
  const [skillTypes, setSkillTypes] = useState<SkillTypeOption[]>([])

  const [form, setForm] = useState({
    empId: 0,
    skillTypeId: 0,
    scoreOutOf100: '' as string | number,
    experienceYear: '' as string | number,
  })

  useEffect(() => {
    if (!branchId) return
    const loadEmployees = async () => {
      try {
        const res = await employeesService.getAllEmployees(branchId, 1, 500)
        const data = (res as any)?.data ?? (res as any)?.Data ?? res
        setEmployees(Array.isArray(data) ? data : [])
      } catch {
        setEmployees([])
      }
    }
    loadEmployees()
  }, [branchId])

  useEffect(() => {
    const loadSkillTypes = async () => {
      try {
        const types = await empSkillService.getSkillTypes()
        setSkillTypes(types ?? [])
      } catch {
        setSkillTypes([])
      }
    }
    loadSkillTypes()
  }, [])

  useEffect(() => {
    if (!isEditing || !id) return
    const load = async () => {
      try {
        setLoading(true)
        const data = await empSkillService.getById(Number(id))
        setForm({
          empId: data.empId ?? 0,
          skillTypeId: (data.skillTypeId ?? data.empSkillTypeId) ?? 0,
          scoreOutOf100: (data.scoreOutOf100 ?? data.empScoreOutOf100) ?? '',
          experienceYear: (data.experienceYear ?? data.empExperienceYear) ?? '',
        })
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? 'Failed to load skill.')
        navigate('/skills')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isEditing, id, navigate])

  const employeeOptions = useMemo(
    () =>
      employees.map((e) => {
        const fullName = [e.empFirstNameEn, e.empSecondNameEn, e.empLastNameEn].filter(Boolean).join(' ').trim()
        const label = fullName ? `${e.empCode ?? ''} — ${fullName}`.trim() : e.empCode ?? `Emp ${e.id}`
        return { value: String(e.id ?? 0), label }
      }).filter((o) => o.value !== '0'),
    [employees]
  )

  const skillTypeOptions = useMemo(
    () =>
      skillTypes.map((t) => {
        const name = (t as any).lookupNameEn ?? (t as any).lookupNameAr ?? (t as any).name ?? `Type ${t.id}`
        return { value: String(t.id ?? 0), label: name }
      }),
    [skillTypes]
  )

  const handleChange = (patch: Partial<typeof form>) => {
    setForm((prev) => ({ ...prev, ...patch }))
  }

  const handleSave = async () => {
    if (!branchId) {
      toast.error('Please select a branch.')
      return
    }
    if (!form.empId || form.empId === 0) {
      toast.error('Please select an employee.')
      return
    }
    const score = typeof form.scoreOutOf100 === 'number' ? form.scoreOutOf100 : parseInt(String(form.scoreOutOf100), 10)
    if (!Number.isFinite(score) || score < 0 || score > 100) {
      toast.error('Score Out Of 100 must be between 0 and 100.')
      return
    }
    const expYear = typeof form.experienceYear === 'number' ? form.experienceYear : parseInt(String(form.experienceYear), 10)
    if (!Number.isFinite(expYear) || expYear < 0) {
      toast.error('Experience Year must be a valid number (0 or more).')
      return
    }

    const payload: SkillVm = {
      id: isEditing && id ? Number(id) : 0,
      empId: form.empId,
      // Frontend field names
      skillTypeId: form.skillTypeId,
      scoreOutOf100: score,
      experienceYear: expYear,
      // Backend field names (Swagger sample)
      empSkillTypeId: form.skillTypeId,
      empScoreOutOf100: score,
      empExperienceYear: expYear,
      branchId,
    }

    try {
      setLoading(true)
      if (isEditing) {
        await empSkillService.update(payload)
        await showSuccess('Updated', 'Skill updated successfully.')
      } else {
        await empSkillService.save(payload)
        await showSuccess('Saved', 'Skill saved successfully.')
      }
      navigate('/skills')
    } catch (e: any) {
      await showError('Save failed', e?.response?.data?.message ?? e?.message ?? 'Failed to save skill.')
    } finally {
      setLoading(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title="Skill" description="Add or edit skill." />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">Please select a branch first.</CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Skill"
        description={isEditing ? 'Edit skill' : 'Add new skill'}
      />

      <Card>
        <CardHeader>
          <CardTitle>Skill</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <SearchableSelect
                options={[{ value: '0', label: 'Select employee' }, ...employeeOptions]}
                value={form.empId ? String(form.empId) : '0'}
                onValueChange={(v) => handleChange({ empId: Number(v) || 0 })}
                placeholder="Select employee"
                searchPlaceholder="Search employee..."
                emptyMessage="No employee found"
                disabled={isEditing}
              />
            </div>
            <div className="space-y-2">
              <Label>Skill Type</Label>
              <SearchableSelect
                options={[{ value: '0', label: 'Select skill type' }, ...skillTypeOptions]}
                value={form.skillTypeId ? String(form.skillTypeId) : '0'}
                onValueChange={(v) => handleChange({ skillTypeId: Number(v) || 0 })}
                placeholder="Select skill type"
                searchPlaceholder="Search skill type..."
                emptyMessage="No skill type found"
              />
            </div>
            <div className="space-y-2">
              <Label>Score Out Of 100 *</Label>
              <Input
                type="number"
                min={0}
                max={100}
                value={form.scoreOutOf100 === '' ? '' : form.scoreOutOf100}
                onChange={(e) => handleChange({ scoreOutOf100: e.target.value ? parseInt(e.target.value, 10) : '' })}
                placeholder="0–100"
              />
            </div>
            <div className="space-y-2">
              <Label>Experience Year *</Label>
              <Input
                type="number"
                min={0}
                value={form.experienceYear === '' ? '' : form.experienceYear}
                onChange={(e) => handleChange({ experienceYear: e.target.value ? parseInt(e.target.value, 10) : '' })}
                placeholder="e.g. 5"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button type="button" variant="outline" onClick={() => navigate('/skills')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Return
            </Button>
            <Button type="button" onClick={handleSave} disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
